import { X } from '@phosphor-icons/react';
import { useEffect, useRef } from 'react';
import { Notification } from '../../types/dbTypes';
import {
  getNotificationLink,
  renderNotificationIcon,
  renderNotificationMessage,
} from '../../utils/NotificationUtils';
import { formatDateWithTime } from '../../utils/DateUtils';
import { useNavigate } from 'react-router-dom';
import useApi from '../../utils/AxiosWrapper';
import { Entity } from '../../types/apiTypes';
import { useUser } from '../../utils/UserContext';
import { HttpStatusCode } from 'axios';

interface NotificationDropdownProps {
  isOpen: boolean;
  notifications: Notification[] | null | undefined;
  isLoading: boolean;
  refetchNotifications: () => void;
}

const NotificationDropdown = ({
  isOpen,
  notifications,
  isLoading,
  refetchNotifications,
}: NotificationDropdownProps) => {
  const notificationsRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();
  const { userId } = useUser();
  const { callProtectedApi } = useApi();
  useEffect(() => {
    if (notifications && notifications.length > 0 && isOpen) {
      const notViewed = notifications
        .filter((notification) => !notification.viewed)
        .map((notification) => notification.id);
      if (notViewed && notViewed?.length > 0) {
        markAsViewed(notViewed);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [notifications, isOpen]);

  const markAsViewed = async (notification_ids: string[]) => {
    try {
      const response = await callProtectedApi(
        `/notifications/viewed/${Entity.Customer}/${userId ?? ''}`,
        'post',
        {
          notification_ids,
        }
      );

      if (response?.status === HttpStatusCode.Ok) {
        refetchNotifications();
      }
    } catch (e: any) {
      console.error(
        'Error occurred while marking notifications as viewed: ',
        e?.message
      );
    }
  };

  const renderNotification = (notification: Notification) => {
    const link = getNotificationLink(notification);
    return (
      <div
        key={notification.id}
        onClick={link ? () => navigate(link) : undefined}
        className={
          link
            ? 'flex w-full cursor-pointer flex-col gap-y-2 border-b-[1px] border-bordergray p-4 hover:bg-ctaactivegreen'
            : 'flex w-full flex-col gap-y-2 border-b-[1px] border-bordergray p-4'
        }
      >
        <div className="flex flex-row items-center space-x-4 text-start">
          {renderNotificationIcon(notification)}
          {renderNotificationMessage(notification)}
        </div>
        {notification.created_at && (
          <div className="flex flex-row items-center justify-end space-x-4">
            <p className="text-sm text-graysecondary">
              {formatDateWithTime(new Date(notification.created_at))}
            </p>
          </div>
        )}
      </div>
    );
  };

  if (!isOpen) return null;

  return (
    <div
      ref={notificationsRef}
      className="absolute right-0 top-[2.85rem] z-50 max-h-96 w-[30rem] overflow-y-auto rounded-lg border-[1px] border-bordergray bg-white shadow-lg drop-shadow-xl"
    >
      <div className="flex items-center justify-between border-b border-gray-200 p-3">
        <h3 className="text-start font-semibold text-gray-800">
          Notifications
        </h3>
        <button className="rounded-full p-1 text-gray-600 hover:bg-gray-200 hover:text-gray-800">
          <X size={20} />
        </button>
      </div>
      {notifications && notifications.length > 0 ? (
        <div className="flex flex-col">
          {notifications.map((notification: Notification) =>
            renderNotification(notification)
          )}
        </div>
      ) : (
        <div className="p-4 text-center text-gray-500">
          {isLoading ? 'Loading...' : 'No notifications'}
        </div>
      )}
    </div>
  );
};

export default NotificationDropdown;
